import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { useEffect, useState, useCallback, useRef } from "react";
import dayjs from "dayjs";
import { formatString } from "../../../../functions/functions";

interface SimpleTripRouteProps {
    startLat: number;
    startLng: number;
    endLat: number;
    endLng: number;
    vehicleType?: string | null;
    driverName?: string | null;
    driverUniqueId?: string | null;
    startAddress?: string | null;
    endAddress?: string | null;
    startTime?: string | number | Date;
    endTime?: string | number | Date;
}

const distanceMetres = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371000;
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Cache estático para persistir as rotas durante a sessão (fora do componente)
let routeCache = new Map<string, any>();

// Função para limpar o cache explicitamente
export const clearRouteCache = () => {
    routeCache.clear();
};

const SimpleTripRoute = ({
    startLat,
    startLng,
    endLat,
    endLng,
    vehicleType = "carro",
    driverName = "N/A",
    driverUniqueId = null,
    startAddress = "Endereço não identificado",
    endAddress = "Endereço não identificado",
    startTime,
    endTime
}: SimpleTripRouteProps) => {
    const map = useMap();
    const routesLibrary = useMapsLibrary("routes");

    // Use any for types to avoid build errors if @types/google.maps is missing
    const [directionsService, setDirectionsService] = useState<any>();
    const [directionsRenderer, setDirectionsRenderer] = useState<any>();

    const markersRef = useRef<any[]>([]);
    const infoWindowsRef = useRef<any[]>([]);

    const getVehicleIcon = useCallback((type: string): any => {
        const g = (window as any).google;
        if (!g || !g.maps) return "A";
        try {
            const formatted = formatString(type || "carro");
            // Webpack-friendly asset loading
            const iconPath = require(`../../../../assets/files/cars/png/${formatted}.png`);
            return {
                url: iconPath,
                scaledSize: new g.maps.Size(40, 40),
                anchor: new g.maps.Point(20, 20),
            };
        } catch (e) {
            try {
                return {
                    url: require(`../../../../assets/files/cars/png/carro.png`),
                    scaledSize: new g.maps.Size(40, 40),
                    anchor: new g.maps.Point(20, 20),
                };
            } catch (inner) {
                return "A";
            }
        }
    }, []);

    const createMarker = useCallback((pos: { lat: number, lng: number }, title: string, content: string, autoOpen: boolean = false) => {
        const g = (window as any).google;
        if (!map || !g || !g.maps) return null;

        const isEnd = title.includes("FIM");
        const titleColor = isEnd ? "#f44336" : "#0FB403";

        const icon = getVehicleIcon(vehicleType ?? "carro");
        const marker = new g.maps.Marker({
            position: pos,
            map,
            title,
            icon: typeof icon === "string" ? undefined : icon,
            label: typeof icon === "string" ? icon : undefined
        });

        const hasName = driverName && driverName !== "null";
        const hasId = driverUniqueId && driverUniqueId !== "null";
        const displayDriver = hasName && hasId 
            ? `${driverName} (${driverUniqueId})` 
            : hasName ? driverName 
            : hasId ? driverUniqueId 
            : "iButton ausente";

        const dateRaw = title.includes("INÍCIO") ? startTime : endTime;

        const infoWindow = new g.maps.InfoWindow({
            content: `<div style="padding: 12px; font-family: 'Roboto', sans-serif; min-width: 220px;">
                <b style="color: ${titleColor}; font-size: 14px; display: block; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px;">${title}</b>
                <p style="margin: 4px 0; font-size: 13px; color: #333; line-height: 1.4;"><b>Endereço:</b> ${content}</p>
                <p style="margin: 4px 0; font-size: 13px; color: #333;"><b>Data:</b> ${dateRaw || ""}</p>
                <p style="margin: 4px 0; font-size: 13px; color: #333;"><b>Motorista:</b> ${displayDriver}</p>
            </div>`
        });

        marker.addListener("click", () => {
            infoWindowsRef.current.forEach(iw => iw.close());
            infoWindow.open(map, marker);
        });

        markersRef.current.push(marker);
        infoWindowsRef.current.push(infoWindow);

        if (autoOpen) {
            setTimeout(() => {
                infoWindow.open(map, marker);
            }, 500);
        }

        return marker;
    }, [map, vehicleType, driverName, driverUniqueId, startTime, endTime, getVehicleIcon]);

    const cleanup = useCallback(() => {
        markersRef.current.forEach(m => m.setMap(null));
        infoWindowsRef.current.forEach(iw => iw.close());
        markersRef.current = [];
        infoWindowsRef.current = [];
    }, []);

    useEffect(() => {
        if (!routesLibrary || !map) return;

        const service = new routesLibrary.DirectionsService();
        const renderer = new routesLibrary.DirectionsRenderer({
            map,
            suppressMarkers: true,
            preserveViewport: false,
            draggable: false,
            polylineOptions: {
                strokeColor: "#0FB403",
                strokeWeight: 6,
                strokeOpacity: 0.8
            }
        });

        setDirectionsService(service);
        setDirectionsRenderer(renderer);

        return () => {
            renderer.setMap(null);
            cleanup();
        };
    }, [routesLibrary, map, cleanup]);

    useEffect(() => {
        if (!directionsService || !directionsRenderer || !map) return;
        if (!startLat || !startLng || !endLat || !endLng) return;

        cleanup();

        // Always draw custom vehicle markers. Start point opens automatically.
        createMarker({ lat: startLat, lng: startLng }, "INÍCIO DA VIAGEM", startAddress ?? "Endereço não identificado", true);
        createMarker({ lat: endLat, lng: endLng }, "FIM DA VIAGEM", endAddress ?? "Endereço não identificado", false);

        const dist = distanceMetres(startLat, startLng, endLat, endLng);

        if (dist < 50) {
            directionsRenderer.setMap(null);
            return;
        }

        directionsRenderer.setMap(map);

        // Chave única para o trajeto (inclui tempo para evitar conflitos entre veículos na mesma rota)
        const cacheKey = `${startLat},${startLng}-${endLat},${endLng}-${startTime}`;

        if (routeCache.has(cacheKey)) {
            directionsRenderer.setDirections(routeCache.get(cacheKey));
            return;
        }

        directionsService
            .route({
                origin: { lat: startLat, lng: startLng },
                destination: { lat: endLat, lng: endLng },
                travelMode: (window as any).google?.maps?.TravelMode?.DRIVING || 'DRIVING',
                optimizeWaypoints: false,
                provideRouteAlternatives: false,
            })
            .then((response: any) => {
                routeCache.set(cacheKey, response);
                directionsRenderer.setDirections(response);
            })
            .catch((err: any) => {
                console.warn("SimpleTripRoute: routing failed, showing markers only.", err?.message ?? err);
                directionsRenderer.setMap(null);
            });

    }, [directionsService, directionsRenderer, startLat, startLng, endLat, endLng, map, createMarker, startAddress, endAddress, cleanup, startTime]);

    return null;
};

export default SimpleTripRoute;
